import { ref } from 'vue'
import { defineStore } from 'pinia'
import { useConfigStore } from '@/stores/config'
import { useTokenStore } from '@/stores/token'
import { usePointsStore } from '@/stores/points'
import { useUsersStore } from '@/stores/users'
import { useProfileStore } from '@/stores/profile'

export const useWsStore = defineStore('ws', () => {
    const configStore = useConfigStore()
    const tokenStore = useTokenStore()
    const pointsStore = usePointsStore()
    const usersStore = useUsersStore()
    const profileStore = useProfileStore()

    const ws = ref({})
    const wsMsg = ref({})
    const wsRestore = ref(false)
    const wsReady = ref(false)
    const wsPing = ref(null)
    const wsWaitingPong = ref(false)
    const wsUser = ref({})
    const wsCar = ref({})
    const wsCurrentCars = ref([])
    const wsNeedReauth = ref(false)
    const wsLastClientMsg = ref({})
    const wsLastServerMsg = ref({})

    function restoreConnection() {
        clearInterval(wsPing.value)

        ws.value.close()
        ws.value = {}
        wsReady.value = false
        wsRestore.value = true

        setTimeout(() => connect(), 5000)
    }

    async function connect() {
        // Проверка наличия подключения к Интернет
        if (navigator.onLine) {
            wsRestore.value = false
            wsWaitingPong.value = false

            // Не работает catch на ошибке при неудачной попытке подключения.
            // Логичный вопрос - а зачем тогда он здесь? Ответ - не знаю. Ну, пускай побудет, мало ли...
            try {
                ws.value = new WebSocket(`${configStore.wsServer}/events`, ['Bearer', tokenStore.access]) || false
            } catch (error) {
                restoreConnection()
            }

            // Точная проверка доступности WS после открытия
            setTimeout(() => {
                if (ws.value.readyState !== 1) {
                    restoreConnection()
                }
            }, 10000)

            ws.value.onopen = () => {
                wsReady.value = true

                wsPing.value = setInterval(() => {
                    if (ws.value.readyState == 1) {
                        if (wsWaitingPong.value) {
                            restoreConnection()
                        } else {
                            ws.value.send(JSON.stringify({ ping: {} }))
                            wsWaitingPong.value = true
                        }
                    } else {
                        restoreConnection()
                    }
                }, 10000)
            }

            ws.value.onmessage = msg => {
                wsMsg.value = msg = JSON.parse(msg.data)

                if ('pong' in msg) {
                    wsWaitingPong.value = false
                } else if ('event' in msg && msg.event?.object?.Object['@type']) {
                    const eventObject = msg.event.object.Object
                    switch (eventObject['@type']) {
                        case 'type.googleapis.com/carsPoints.Point':
                            if (wsCurrentCars.value.includes(eventObject.car)) pointsStore.readPoints(eventObject)
                            break

                        case 'type.googleapis.com/users.User':
                            wsUser.value = eventObject
                            if (eventObject.sessionIsDeleted && profileStore.uuid == eventObject.uuid) {
                                wsNeedReauth.value = true
                                if (wsReady.value) {
                                    ws.close()
                                    clearInterval(wsPing.value)
                                }
                            } else if (eventObject.uuid == profileStore.profile.uuid) {
                                profileStore.profile = eventObject
                            } else if (usersStore.users[eventObject.uuid]) {
                                usersStore.user = eventObject
                            }
                            break
                    }
                } else if (msg?.error?.details[0]?.value == 'ETokenExpired') {
                    profileStore.readProfile().then(res => {
                        if (res.uuid && !wsRestore.value) {
                            restoreConnection()
                            return
                        }
                    })
                }
            }

            ws.onerror = error => {
                console.log('ERROR: ', error)
                clearInterval(wsPing.value)
                if (!wsRestore.value) {
                    restoreConnection()
                }
            }

            ws.onclose = msg => {
                console.log('CLOSE: ', error)
                clearInterval(wsPing.value)
                if (msg.wasClean) return
                if (!wsRestore.value) restoreConnection()
            }
        } else {
            setTimeout(() => connect(), 5000)
        }
    }

    async function openWs() {
        if (typeof WebSocket !== 'undefined') {
            if (tokenStore.access) {
                connect()
            } else {
                wsMsg.value = { success: false, text: 'EUnauthRequest' }
            }
        } else {
            wsMsg.value = { success: false, text: 'EWSNotSuport' }
        }
    }

    function sendMsg(msg) {
        if (ws.value.readyState == 1) {
            wsLastClientMsg.value = msg
            ws.value.send(msg)
        } else {
            restoreConnection()
        }
    }

    return {
        ws,
        wsMsg,
        wsRestore,
        wsReady,
        wsPing,
        wsUser,
        wsCar,
        wsCurrentCars,
        wsNeedReauth,
        wsLastClientMsg,
        wsLastServerMsg,
        connect,
        openWs,
        sendMsg
    }
})
